<script>
  import { getContext, onMount } from "svelte";
  import { useNavigate } from "svelte-navigator";
  import { projectListStore } from "@stores/damProjectsStore.js";
  import { uiFeatureStore } from "@stores/uiFeatureStore.js";
  import { ROUTES } from "@utils/routes.js";
  import Modal from "@components/Modal.svelte";
  import {
    DataTable,
    Grid,
    Row,
    Column,
  } from "carbon-components-svelte";

  import Header from "../../../lib/components/Header.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { fly } from "svelte/transition";
  import ProjectColumn from "@components/ProjectColumn.svelte";
  import SeamlessGridHeader from "@components/common/SeamlessGridHeader.svelte";

  // import TextArea from "@components/common/forms/TextAreaInput.svelte";
  import SidePanel from "@components/SidePanel.svelte";
  import IntelligenceCard from "@components/IntelligenceCard.svelte";
  import {
    Tooltip,
    TextInput,
    SectionWrapper,
    BadgeColor,
    Button,
  } from "paxray-components-svelte";
  import SearchNew from "@components/searchNew.svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import EmptyHome from "./components/EmptyHome.svelte";
  import CreateProjectModal from "./components/CreateProjectModal.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";
  import { CLOSE_ICON, EDIT_ICON, TRASH_ICON } from "@utils/icons.js";

  const navigate = useNavigate();
  const role = getContext("role");
  $: isAdmin = $role?.role === "admin";

  const { getProjects, getProjectProgress } = projectListStore;
  $: ({ projectsList = { analysis: [], running: [] } } = $projectListStore);

  let potentialScoutProjects = { analysis: [], running: [] };
  let monitoringProjects = [
    {
      id: "software-monitoring",
      name: "Software Intelligence",
      status: "No data",
      type: "Software Catalog",
      isMonitoring: true,
      metric: 0,
      metricLabel: "projects",
      kpis: [
        { label: "Projects", value: 0 },
        { label: "Applications", value: 0 },
        { label: "Snapshots", value: 0 },
      ],
    },
    {
      id: "process-monitoring",
      name: "Process Intelligence",
      status: "No data",
      type: "Process Catalog",
      isMonitoring: true,
      isProcessMonitoring: true,
      metric: 0,
      metricLabel: "processes",
      kpis: [
        { label: "Projects", value: 0 },
        { label: "Processes", value: 0 },
        { label: "Tasks", value: 0 },
      ],
    },
  ];
  let projectSearch = "";
  let loading = true;
  let showHomeGuide = false;
  let homeGuideStep = 0;

  onMount(() =>
    registerViewTour(() => {
      homeGuideStep = 0;
      showHomeGuide = true;
    }),
  );
  // Ohne Projekte und ohne Digital-Twin-Daten zeigt Home nur den Einstieg.
  $: isEmptyHome =
    !projects.analysis.length &&
    !projects.running.length &&
    !monitoringProjects.some((project) => project.status === "Active");
  $: canCreateProject =
    $uiFeatureStore.platformFeatures.newProject && typeOptions.length;
  $: homeGuideSteps = isEmptyHome ? emptyHomeGuideSteps : defaultHomeGuideSteps;
  $: emptyHomeGuideSteps = [
    {
      target: "#app-side-nav .home_icon",
      title: "Home",
      text: "This is your home page. Come back here anytime to see all your initiatives.",
      placement: "right",
    },
    {
      target: ".header_breadcrumb",
      title: "Where you are",
      text: "Shows where you are. Click a part of the path to go back.",
    },
    {
      target: '[data-guide="process-framework"] button',
      title: "Global Process Framework",
      text: "Your company's shared process structure. Setting it up first makes your initiatives easier to configure.",
    },
    ...(canCreateProject
      ? [
          {
            target: '[data-guide="new-project"] button',
            title: "Start your first initiative",
            text: "Choose an initiative type and set up your analysis in a few steps.",
          },
        ]
      : []),
    ...($uiFeatureStore.platformFeatures.guidedTours
      ? [
          {
            target:
              '#app-side-nav [aria-label="Start tour for the current view"]',
            title: "Tours on every page",
            text: "Every page has its own tour. Start it here whenever you need help.",
            placement: "right",
          },
        ]
      : []),
    {
      target: '#app-side-nav [aria-label="Open account menu"]',
      title: "Your account",
      text: $uiFeatureStore.userMenu.showAccountSettings
        ? "Open your account menu to manage your settings or log out."
        : "Open your account menu to log out.",
      placement: "right",
    },
  ];
  $: defaultHomeGuideSteps = [
    {
      target: '[data-guide="process-framework"] button',
      title: "Global Process Framework",
      text: "Your company's shared process structure. Every initiative builds on it.",
    },
    ...(canCreateProject
      ? [
          {
            target: '[data-guide="new-project"] button',
            title: "Start a new initiative",
            text: "Choose an initiative type and set up your analysis in a few steps.",
          },
        ]
      : []),
    {
      target: '[data-project-column="Running"] > .seamless_grid_header',
      title: "Running",
      text: "Initiatives still being prepared. Open one to continue where you left off.",
    },
    {
      target: '[data-project-column="Analysis"] > .seamless_grid_header',
      title: "Ready for analysis",
      text: "Data collection is complete. Open an initiative to explore its results.",
    },
    {
      target: '[data-project-column="Completed"] > .seamless_grid_header',
      title: "Completed",
      text: "Completed or paused initiatives. You can still open them anytime.",
    },
    ...(visibleMonitoringProjects.some((project) => !project.disabled)
      ? [
          {
            target: '[data-guide="digital-twins"]',
            title: "Insights across initiatives",
            text: "A combined view of what your completed initiatives revealed.",
            placement: "right",
          },
        ]
      : []),
  ];
  $: projects = {
    analysis: [
      ...(projectsList.analysis || []),
      ...potentialScoutProjects.analysis,
    ],
    running: [
      ...(projectsList.running || []),
      ...potentialScoutProjects.running,
    ],
  };
  $: showAllProjectsAsArchived =
    $uiFeatureStore.mainAreas.showAllProjectsAsArchived;
  $: visibleProjects = {
    analysis: projects.analysis.filter(
      (project) =>
        projectAreaEnabled(project) &&
        !showAllProjectsAsArchived &&
        !isArchivedProject(project) &&
        matchesProjectSearch(project),
    ),
    running: projects.running.filter(
      (project) =>
        projectAreaEnabled(project) &&
        !showAllProjectsAsArchived &&
        !isArchivedProject(project) &&
        matchesProjectSearch(project),
    ),
  };
  $: visibleMonitoringProjects = monitoringProjects
    .map((project) => {
      const enabled = project.isProcessMonitoring
        ? $uiFeatureStore.mainAreas.processIntelligence
        : $uiFeatureStore.mainAreas.systemIntelligence;
      return {
        ...project,
        disabled: !enabled,
        status: enabled ? project.status : "Not included",
      };
    })
    .filter(matchesProjectSearch);
  $: visibleArchivedProjects = [...projects.analysis, ...projects.running]
    .filter(
      (project) =>
        projectAreaEnabled(project) &&
        (showAllProjectsAsArchived || isArchivedProject(project)) &&
        matchesProjectSearch(project),
    )
    .map((project) => ({
      ...project,
      archived: true,
      disabled: !isAdmin,
    }));
  let deleteProjectId = 0;
  let createProjectModalOpen = false;
  let deleteProjectModalOpen = false;
  let cloneProjectId;
  let cloneProjectModalOpen = false;
  let cloneProjectName = "";
  let shareProjectId = 0;
  let shareProjectModalOpen = false;
  let shareProjectData = [];
  let shareProjectViewers = [];
  let shareProjectEditors = [];
  let stepNames = [
    "Project Setup",
    "Application Scoping",
    "Anonymization Rules",
    "Main Recording",
    "Analysis",
  ];
  const projectStages = [
    { key: "SETUP", label: "Setup" },
    { key: "SCOPING", label: "Scoping" },
    { key: "TASK_REGISTRATION", label: "Discovery" },
    { key: "DATA_COLLECTION", label: "Discovery Extended" },
  ];

  function projectStageColor(stageKey, currentStage) {
    const currentIndex = projectStages.findIndex(
      (stage) => stage.key === String(currentStage || "").toUpperCase(),
    );
    const stageIndex = projectStages.findIndex(
      (stage) => stage.key === stageKey,
    );

    if (currentIndex < 0 || stageIndex > currentIndex) return "grey";
    if (stageIndex === currentIndex) return "yellow";
    return "green";
  }

  const projectStatusLabel = (project) =>
    project?.isPotentialScout
      ? project.status === "FINISHED"
        ? "Finished"
        : project.status === "PENDING"
          ? "Pending"
          : "Running"
      : "Online";
  let deleteProjectType = "running";
  let deleteProjectIsPotentialScout = false;
  let projectToEdit = "";
  let deleteProjectName = "";
  // Loeschen erst, wenn der Name exakt eingetippt ist - Schutz vor dem
  // falschen Klick in der Projektliste.
  let deleteProjectConfirmName = "";
  $: deleteProjectNameMismatch =
    deleteProjectConfirmName.trim() !== deleteProjectName;
  let openPanel = false;
  let currentProject = null;
  const allTypeOptions = [
    { key: "taskAnalysis", value: "Task Analysis" },
    { key: "potentialScout", value: "Potential Scout" },
    // Admin-only, creates a Task Analysis project prefilled with sample data.
    { key: "technicalCheck", value: "Technical Check" },
  ];
  $: typeOptions = allTypeOptions.filter((option) =>
    option.key === "potentialScout"
      ? $uiFeatureStore.mainAreas.potentialScoutProjects
      : option.key === "technicalCheck"
        ? isAdmin && $uiFeatureStore.mainAreas.taskMiningProjects
        : $uiFeatureStore.mainAreas.taskMiningProjects,
  );
  let sendSurveyModalOpen = false;
  let sendingSurveyInvitations = false;
  let surveyActionError = "";
  let endSurveyModalOpen = false;
  let endingSurvey = false;
  $: currentSurveyInvitationCounts = surveyInvitationCounts(currentProject);
  $: currentSurveyOverview = surveyOverview(currentProject);
  $: currentSurveySourceProject = surveySourceProject(currentProject);

  function matchesProjectSearch(project) {
    const query = projectSearch.trim().toLowerCase();
    if (!query) return true;
    return [
      projectDisplayName(project),
      project?.status,
      project?.type,
      project?.description,
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));
  }

  function projectAreaEnabled(project) {
    return project?.isPotentialScout
      ? $uiFeatureStore.mainAreas.potentialScoutProjects
      : $uiFeatureStore.mainAreas.taskMiningProjects;
  }

  function isArchivedProject(project) {
    return ["ARCHIVED", "COMPLETED"].includes(
      String(project?.status || "").toUpperCase(),
    );
  }

  function projectDisplayName(project) {
    if (
      project?.isPotentialScout &&
      String(project?.name || "").startsWith(
        "Operational Excellence Pulse 2026",
      )
    ) {
      return "Demo Potential Scout";
    }
    if (project?.name !== "Demo") return project?.name || "";
    return project?.isPotentialScout
      ? "Demo Potential Scout"
      : "Demo Task Mining";
  }

  let deleteProject = (id, projectType, isPotentialScout = false) => {
    deleteProjectType = projectType;
    deleteProjectIsPotentialScout = isPotentialScout;
    deleteProjectId = id;
    deleteProjectConfirmName = "";
    deleteProjectModalOpen = true;
    console.log("DELETE PROJECT", { projects, projectType, id });
    deleteProjectName = projects[projectType].find((x) => x.id == id).name;
  };

  let confirmDeleteProject = () => {
    const endpoint = deleteProjectIsPotentialScout
      ? `/api/knextractor/surveys/${deleteProjectId}`
      : `/api/damprojects/projects/${deleteProjectId}`;
    fetchWithSession(endpoint, {
      method: "DELETE",
      suppressSuccessToast: true,
    })
      .then((res) => res.json())
      .then(async (res) => {
        if (res.ok) {
          if (deleteProjectIsPotentialScout) {
            await getPotentialScoutProjects();
          } else {
            await getProjects();
          }
        }
        openPanel = false;
        currentProject = null;
        deleteProjectModalOpen = false;
      });
  };

  let cloneProject = (id) => {
    cloneProjectId = id;
    cloneProjectModalOpen = true;
  };

  let confirmCloneProject = () => {
    fetchWithSession(`/api/analysis/cloneDAMProject`, {
      method: "POST",
      body: JSON.stringify({
        idproject: cloneProjectId,
        projectname: cloneProjectName,
      }),
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.success) {
          projects.running.push(res.data);
          cloneProjectModalOpen = false;
        }
      });
  };

  let shareProject = (id) => {
    shareProjectModalOpen = true;
    shareProjectId = id;
    fetchWithSession(`/api/damprojects/shareDAMProject/${shareProjectId}`)
      .then((res) => res.json())
      .then((res) => {
        if (res.success) {
          shareProjectData = res.data;
          shareProjectViewers = res.data
            .filter((x) => x.viewer)
            .map((x) => x.id);
          shareProjectEditors = res.data
            .filter((x) => x.editor)
            .map((x) => x.id);
        }
      });
  };

  let saveShareProject = () => {
    fetchWithSession(`/api/damprojects/shareDAMProject/${shareProjectId}`, {
      method: "POST",
      body: JSON.stringify({
        viewers: shareProjectViewers,
        editors: shareProjectEditors,
      }),
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.success) {
          shareProjectModalOpen = false;
        }
      });
  };

  function getProjectsList() {
    const hasData =
      projectsList?.analysis?.length > 0 || projectsList?.running?.length > 0;
    if (!hasData) {
      return getProjects();
    }
    return Promise.resolve();
  }

  function editProject(project) {
    projectToEdit = project;
    createProjectModalOpen = true;
  }

  function openCreateProject() {
    showHomeGuide = false;
    projectToEdit = "";
    createProjectModalOpen = true;
  }

  async function handleProjectCreated(event) {
    const { type, id } = event.detail;
    if (type === "potentialScout") {
      await getPotentialScoutProjects();
      navigate(`${ROUTES.knex.potentialScope.dashboard}?survey=${id}`);
      return;
    }
    getProjects();
    navigate(`${ROUTES.dam.progress}?idproject=${id}`);
  }

  function handleProjectUpdated(event) {
    const { id, data } = event.detail;
    projects = {
      ...projects,
      ...Object.fromEntries(
        Object.entries(projects).map(([type, projs]) => [
          type,
          projs.map((proj) => (proj.id === id ? { ...proj, ...data } : proj)),
        ]),
      ),
    };
    if (currentProject?.id === id) {
      currentProject = { ...currentProject, ...data };
    }
  }

  async function handleOpenSidePanel(project) {
    if (project.isMonitoring) {
      navigate(
        project.isProcessMonitoring
          ? ROUTES.monitoring.processRoot
          : ROUTES.monitoring.root,
      );
      return;
    }
    if (currentProject && currentProject.id === project.id) {
      openPanel = false;
      currentProject = null;
      return;
    }
    openPanel = true;
    currentProject = project;
    if (project.isPotentialScout) {
      currentProject = await loadPotentialScoutDetails(project);
    }
    console.log("Open project details for project:", project);
  }

  async function loadPotentialScoutDetails(project) {
    let details = {
      ...project,
      type: surveyTypeLabel(project),
      isPotentialScout: true,
    };
    try {
      const response = await fetchWithSession(
        `/api/knextractor/surveys/${project.id}`,
      );
      if (response.ok) {
        const surveyDetails = await response.json();
        details = {
          ...details,
          ...surveyDetails,
          type: surveyTypeLabel(surveyDetails),
          isPotentialScout: true,
        };
      }
    } catch (error) {
      console.warn("Potential Scout details are unavailable:", error);
    }
    try {
      const snapshotResponse = await fetchWithSession(
        `/api/knextractor/analysis/${project.id}/snapshot`,
      );
      if (snapshotResponse.ok) {
        const snapshotData = await snapshotResponse.json();
        details = {
          ...details,
          analysisSnapshot: snapshotData.snapshot || null,
        };
      }
    } catch (error) {
      console.warn("Potential Scout analysis snapshot is unavailable:", error);
    }
    return details;
  }

  function editPotentialScout(project) {
    if (!project || project.status === "FINISHED") return;
    navigate(`${ROUTES.knex.form}?survey=${project.id}`);
  }

  function previewPotentialScout(project) {
    if (!project) return;
    window.open(
      `${ROUTES.knex.survey}?preview=${project.id}`,
      "_blank",
      "noopener,noreferrer",
    );
  }

  async function sendPotentialScoutInvitations() {
    if (!currentProject || sendingSurveyInvitations) return;
    sendingSurveyInvitations = true;
    surveyActionError = "";
    try {
      const response = await fetchWithSession(
        `/api/knextractor/surveys/${currentProject.id}/send-emails`,
        { method: "POST" },
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Invitation sending failed");
      sendSurveyModalOpen = false;
      await getPotentialScoutProjects();
      currentProject = await loadPotentialScoutDetails(currentProject);
    } catch (error) {
      surveyActionError = error.message || "Invitation sending failed";
    } finally {
      sendingSurveyInvitations = false;
    }
  }

  async function endPotentialScoutSurvey() {
    if (!currentProject || endingSurvey) return;
    endingSurvey = true;
    surveyActionError = "";
    try {
      const response = await fetchWithSession(
        `/api/knextractor/surveys/${currentProject.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            enabled: false,
            end_date: new Date().toISOString().slice(0, 10),
          }),
        },
      );
      if (!response.ok) throw new Error("Ending survey failed");
      endSurveyModalOpen = false;
      openPanel = false;
      currentProject = null;
      await getPotentialScoutProjects();
    } catch (error) {
      surveyActionError = error.message || "Ending survey failed";
    } finally {
      endingSurvey = false;
    }
  }

  function surveyInvitationCounts(project) {
    const users = project?.survey_users || [];
    return {
      recipients: users.length,
      outstanding: users.filter((entry) => !entry.invitation_email_sent).length,
      pending: users.filter(
        (entry) => entry.invitation_email_sent && !entry.survey_answered,
      ).length,
      completed: users.filter((entry) => entry.survey_answered).length,
    };
  }

  function surveyOverview(project) {
    const users = project?.survey_users || [];
    const invitations = users.length;
    const responses = users.filter((entry) => entry.survey_answered).length;
    return {
      invitations,
      responses,
      responseRate: invitations
        ? Math.round((responses / invitations) * 100)
        : 0,
      tasks: isTaskSurvey(project)
        ? project?.task_feedback_config?.tasks?.length || 0
        : project?.survey_tasks?.length || 0,
      clusters: project?.analysisSnapshot?.cluster_total ?? "-",
      kpis: project?.task_feedback_config?.kpis?.length || 0,
      timeframe: `${project?.start_date || "-"} - ${project?.end_date || "-"}`,
    };
  }

  // Surveys tied to a task analysis project carry the "Task Survey" tag,
  // standalone surveys keep "Potential Scout". A task survey is configured in
  // the Task KPI Setup of its project, not in the Potential Scout form builder.
  const isTaskSurvey = (survey) => survey?.survey_type === "task_feedback";

  const surveyTypeLabel = (survey) =>
    isTaskSurvey(survey) ? "Task Survey" : "Potential Scout";

  function openTaskSurveySetup(project) {
    const sourceId = project?.task_feedback_config?.source_project_id;
    if (!sourceId) return;
    navigate(`${ROUTES.dam.taskfrequencymapping}?idproject=${sourceId}`);
  }

  function surveySourceProject(project) {
    const sourceId = project?.task_feedback_config?.source_project_id;
    if (!sourceId) return null;
    return (
      [...(projectsList.analysis || []), ...(projectsList.running || [])].find(
        (candidate) => candidate.id === sourceId,
      ) || null
    );
  }

  function surveyLanguageLabel(language) {
    return (
      {
        en: "English (ENG)",
        de: "Deutsch (GER)",
        es: "Español (SPA)",
        it: "Italiano (ITA)",
      }[language] || "English (ENG)"
    );
  }

  async function getPotentialScoutProjects() {
    try {
      // Potential Scout ist ein Nebenmodul: laeuft der Dienst nicht, bleibt die
      // Spalte einfach leer, kein Fehler-Toast fuer den Nutzer.
      const response = await fetchWithSession("/api/knextractor/surveys", {
        suppressErrorToast: true,
      });
      if (!response.ok) return;
      const surveys = await response.json();
      const projectSurveys = (Array.isArray(surveys) ? surveys : []).map(
        (survey) => ({
          ...survey,
          type: surveyTypeLabel(survey),
          isPotentialScout: true,
        }),
      );
      potentialScoutProjects = {
        analysis: projectSurveys.filter(
          (survey) => survey.status === "FINISHED",
        ),
        running: projectSurveys.filter(
          (survey) => survey.status !== "FINISHED",
        ),
      };
    } catch (error) {
      console.warn("Potential Scout projects are unavailable:", error);
    }
  }

  async function getMonitoringProject() {
    try {
      const response = await fetchWithSession("/api/monitoring/summary", {
        suppressErrorToast: true,
      });
      if (!response.ok) return;
      const summary = await response.json();
      monitoringProjects = [
        {
          id: "software-monitoring",
          name: "Software Intelligence",
          status: summary.source_project_count > 0 ? "Active" : "No data",
          type: "Software Catalog",
          isMonitoring: true,
          metric: summary.source_project_count || 0,
          metricLabel: "projects",
          kpis: [
            { label: "Projects", value: summary.source_project_count || 0 },
            { label: "Applications", value: summary.application_count || 0 },
            { label: "Snapshots", value: summary.snapshot_count || 0 },
          ],
        },
        {
          id: "process-monitoring",
          name: "Process Intelligence",
          status: summary.process_count > 0 ? "Active" : "No data",
          type: "Process Catalog",
          isMonitoring: true,
          isProcessMonitoring: true,
          metric: summary.process_count || 0,
          metricLabel: "processes",
          kpis: [
            { label: "Projects", value: summary.source_project_count || 0 },
            { label: "Processes", value: summary.process_count || 0 },
            { label: "Tasks", value: summary.process_task_count || 0 },
          ],
        },
      ];
    } catch (error) {
      console.warn("Monitoring summary is unavailable:", error);
    }
  }

  async function handleOpenProject(project = currentProject) {
    if (!project) return;
    if (project.disabled) return;
    showHomeGuide = false;
    if (project.isMonitoring) {
      navigate(
        project.isProcessMonitoring
          ? ROUTES.monitoring.processRoot
          : ROUTES.monitoring.root,
      );
      return;
    }
    if (project.isPotentialScout) {
      navigate(`${ROUTES.knex.potentialScope.dashboard}?survey=${project.id}`);
      return;
    }
    const progressLoaded = await getProjectProgress(project.id, {
      force: true,
    });
    if (!progressLoaded) {
      console.warn(
        `Project progress could not be preloaded for project ${project.id}. Navigating anyway.`,
      );
    }
    // navigate(`${ROUTES.dam.progress}?idproject=${currentProject.id}&currentStep=${currentProject.step}`);
    if (project.analysisid) {
      navigate(
        `${ROUTES.dam.progress}?analysisid=${project.analysisid}&idproject=${project.id}`,
      );
    } else {
      navigate(`${ROUTES.dam.progress}?idproject=${project.id}`);
    }
  }

  onMount(async () => {
    loading = true;
    await Promise.all([
      getProjectsList(),
      getPotentialScoutProjects(),
      getMonitoringProject(),
    ]);
    loading = false;
  });

  $$restProps;
</script>

<Header section="" breadcrumb="Home">
  <!--
  <div class="header_project_search">
    <SearchNew bind:searchTerm={projectSearch} placeholder="Projects" />
  </div>
  -->
  <div class="header_new_project" data-guide="process-framework">
    <Button
      on:click={() => navigate(ROUTES.dam.companySettings.processFramework)}
      kind="secondary"
      text="Global Process Framework"
    />
  </div>
  {#if canCreateProject}
    <div class="header_new_project" data-guide="new-project">
      <Button
        on:click={openCreateProject}
        kind="secondary"
        text="New Project"
      />
    </div>
  {/if}
</Header>

{#if !loading}
  <SpotlightGuide
    bind:open={showHomeGuide}
    bind:currentStep={homeGuideStep}
    steps={homeGuideSteps}
    allowInteraction={false}
  />
{/if}

<SectionWrapper
  fullscreen
  seamless={true}
  seamlessPadding={false}
  {loading}
  loadingTitle="Loading your projects."
  loadingHint="Please wait while your workspace is prepared."
>
  <div slot="main" class="main">
    {#if isEmptyHome}
      <EmptyHome
        {canCreateProject}
        on:setupFramework={() =>
          navigate(ROUTES.dam.companySettings.processFramework)}
        on:createProject={openCreateProject}
        on:startTour={() => {
          homeGuideStep = 0;
          showHomeGuide = true;
        }}
      />
    {:else}
      <div class="projects_overview_layout">
        {#if visibleMonitoringProjects.length}
          <section class="digital_twin_section">
            <SeamlessGridHeader header="Digital Twin" />
            <div class="intelligence_grid" data-guide="digital-twins">
              {#each visibleMonitoringProjects as project}
                <IntelligenceCard {project} onClick={handleOpenProject} />
              {/each}
            </div>
          </section>
        {/if}
        <ProjectColumn
          projects={visibleProjects.running}
          headerName="Running"
          tooltipText="Projects that are currently being prepared."
          accentColor="#e5e8a8"
          {handleOpenSidePanel}
          {handleOpenProject}
          {currentProject}
        />
        <ProjectColumn
          projects={visibleProjects.analysis}
          headerName="Analysis"
          tooltipText="Projects that are ready for analysis."
          accentColor="#b7e3bd"
          {handleOpenSidePanel}
          {handleOpenProject}
          {currentProject}
        />
        <ProjectColumn
          projects={visibleArchivedProjects}
          headerName="Completed"
          tooltipText="Archived and completed projects."
          accentColor="#abcfe8"
          {handleOpenSidePanel}
          {handleOpenProject}
          {currentProject}
        />
      </div>
    {/if}

    <SidePanel open={openPanel}>
      <div slot="header" class="side-header">
        <div class="side-header-name">
          <h3
            class="side-project-title"
            title={projectDisplayName(currentProject)}
          >
            {projectDisplayName(currentProject)}
          </h3>
          <div class="side-header-right">
            <span
              class="project-status-indicator"
              title={projectStatusLabel(currentProject)}
              aria-label={projectStatusLabel(currentProject)}
              role="img"
            ></span>
            {#if currentProject?.isPotentialScout}
              <Button
                kind="secondary"
                iconDescription="Delete project"
                icon={TRASH_ICON}
                size="small"
                on:click={() =>
                  deleteProject(
                    currentProject.id,
                    currentProject.status === "FINISHED"
                      ? "analysis"
                      : "running",
                    true,
                  )}
              />
              {#if isTaskSurvey(currentProject)}
                <Button
                  icon={EDIT_ICON}
                  kind="secondary"
                  size="small"
                  on:click={() => openTaskSurveySetup(currentProject)}
                />
              {:else if currentProject.status !== "FINISHED"}
                <Button
                  icon={EDIT_ICON}
                  size="small"
                  kind="secondary"
                  on:click={() => editPotentialScout(currentProject)}
                />
              {/if}
            {:else}
              <Button
                icon={EDIT_ICON}
                kind="secondary"
                size="small"
                on:click={() => editProject(currentProject)}
              />
            {/if}
            <Button
              icon={CLOSE_ICON}
              kind="secondary"
              size="small"
              iconDescription="Close project details"
              on:click={() => {
                openPanel = false;
                currentProject = null;
              }}
            />
          </div>
        </div>
      </div>
      <div slot="container" class="side-container">
        {#if currentProject?.isPotentialScout}
          <div>
            <div class="survey_overview_list">
              <div>
                <p class="side-title">Responses</p>
                <p class="side-content">
                  {currentSurveyOverview.responses} / {currentSurveyOverview.invitations}
                </p>
              </div>
              <div>
                <p class="side-title">Response Rate</p>
                <p class="side-content">
                  {currentSurveyOverview.responseRate}%
                </p>
              </div>
              <div>
                <p class="side-title">Tasks</p>
                <p class="side-content">{currentSurveyOverview.tasks}</p>
              </div>
              <div>
                <p class="side-title">
                  {isTaskSurvey(currentProject)
                    ? "Feedback questions"
                    : "Clusters"}
                </p>
                <p class="side-content">
                  {isTaskSurvey(currentProject)
                    ? currentSurveyOverview.kpis
                    : currentSurveyOverview.clusters}
                </p>
              </div>
              <div>
                <p class="side-title">Survey Timeframe</p>
                <p class="side-content">{currentSurveyOverview.timeframe}</p>
              </div>
            </div>
          </div>
          <div>
            <div class="side_title_with_tooltip">
              <p class="side-title">Invitations</p>
              <Tooltip>
                <strong>Outstanding</strong> invitations have not been sent yet.<br
                />
                <strong>Pending</strong> invitations have been sent, but the
                survey has not been completed yet.<br />
                <strong>Completed</strong> invitations have a finished survey response.
              </Tooltip>
            </div>
            <div class="survey_invitation_badges">
              <BadgeColor
                color="yellow"
                text={`${currentSurveyInvitationCounts.outstanding} outstanding`}
                small
              />
              <BadgeColor
                color="black"
                text={`${currentSurveyInvitationCounts.pending} pending`}
                small
              />
              <BadgeColor
                color="green"
                text={`${currentSurveyInvitationCounts.completed} completed`}
                small
              />
            </div>
            {#if currentProject.status !== "FINISHED" && currentSurveyInvitationCounts.outstanding > 0}
              <div class="survey_invitation_action">
                <Button
                  on:click={() => {
                    surveyActionError = "";
                    sendSurveyModalOpen = true;
                  }}
                  kind="secondary"
                  text="Send Invitations now"
                />
              </div>
            {/if}
          </div>
          <div>
            <p class="side-title">Survey Language</p>
            <p class="side-content">
              {surveyLanguageLabel(currentProject.language)}
            </p>
          </div>
          {#if currentSurveySourceProject}
            <div>
              <p class="side-title">Task Analysis Project</p>
              <button
                class="side-content side_link"
                on:click={() => handleOpenProject(currentSurveySourceProject)}
              >
                {projectDisplayName(currentSurveySourceProject)}
              </button>
            </div>
          {/if}
        {:else if currentProject?.projectContacts?.length > 0}
          <div class="contact-persons-info">
            <p class="side-title">Contact Persons</p>
            {#each currentProject?.projectContacts || [] as person, index}
              <p class="side-content">
                <strong>
                  {index === 0
                    ? "Controller"
                    : index === 1
                      ? "Data Privacy Officer"
                      : index === 2
                        ? "IT Contact"
                        : "Contact Person"}:
                </strong>
                {person.name + " (" + person.email + ")"}
              </p>
            {/each}
          </div>
        {/if}
        {#if !currentProject?.isPotentialScout && currentProject?.description}
          <div>
            <p class="side-title">Description</p>
            <p class="side-content">{currentProject?.description}</p>
          </div>
        {/if}
        {#if !currentProject?.isPotentialScout}
          {#if currentProject?.departments?.length > 0}
            <div>
              <p class="side-title">
                {currentProject.departments.length === 1
                  ? "Department"
                  : "Departments"}
              </p>
              <div style="display:flex; flex-wrap:wrap; gap:0.5rem">
                {#each currentProject.departments as dept}
                  <BadgeColor color="grey" text={dept.name} small />
                {/each}
              </div>
            </div>
          {/if}
          {#if currentProject?.damTeams?.length > 0}
            <div>
              <p class="side-title">
                Teams
                <!-- <span>
              ({(currentProject?.damTeams || []).reduce(
                (total, team) => total + (team?.users?.length || 0),
                0,
              )}
              Users)
            </span>-->
              </p>
              <div style="display:flex; flex-wrap:wrap; gap:0.5rem">
                {#each currentProject.damTeams as team}
                  <BadgeColor color="grey" text={team.name} small />
                {/each}
              </div>
            </div>
          {/if}
          {#if currentProject?.status !== "Analysis"}
            <div>
              <p class="side-title">Progress</p>
              <div style="display:flex; flex-wrap:wrap; gap:0.5rem">
                {#each projectStages as stage}
                  <BadgeColor
                    color={projectStageColor(
                      stage.key,
                      currentProject.current_stage,
                    )}
                    text={stage.label}
                    small
                  />
                {/each}
              </div>
            </div>
          {/if}
        {/if}
      </div>
      <div slot="footer" class="side_footer_actions">
        <Button
          on:click={(e) => {
            e.stopPropagation();
            handleOpenProject();
          }}
          kind="secondary"
          text="Open Project"
          disabled={currentProject?.disabled}
        />
        {#if currentProject?.isPotentialScout}
          {#if !isTaskSurvey(currentProject)}
            <Button
              on:click={() => previewPotentialScout(currentProject)}
              kind="secondary"
              text="Preview survey"
            />
          {/if}
          {#if currentProject.status !== "FINISHED"}
            <Button
              on:click={() => {
                surveyActionError = "";
                endSurveyModalOpen = true;
              }}
              kind="secondary"
              text="End survey"
            />
          {/if}
        {/if}
      </div>
    </SidePanel>
  </div>
</SectionWrapper>

<Modal
  bind:open={sendSurveyModalOpen}
  modalHeading="Send invitations now?"
  actions
  width={440}
  primaryButtonText={sendingSurveyInvitations
    ? "Sending..."
    : "Send Invitations now"}
  secondaryButtonText="Cancel"
  primaryButtonValidations={sendingSurveyInvitations}
  primaryButtonAction={sendPotentialScoutInvitations}
  secondaryButtonAction={() => (sendSurveyModalOpen = false)}
>
  <div class="survey_send_warning">
    <strong>The outstanding invitations will be sent immediately.</strong>
    <p>Invitations that have already been sent will not be sent again.</p>
    {#if surveyActionError}<p class="orange">{surveyActionError}</p>{/if}
  </div>
</Modal>

<Modal
  bind:open={endSurveyModalOpen}
  modalHeading="End survey?"
  actions
  width={440}
  primaryButtonText={endingSurvey ? "Ending survey..." : "End survey"}
  secondaryButtonText="Cancel"
  primaryButtonValidations={endingSurvey}
  primaryButtonAction={endPotentialScoutSurvey}
  secondaryButtonAction={() => (endSurveyModalOpen = false)}
>
  <div class="survey_send_warning">
    <strong>Detailed analysis will start after confirmation.</strong>
    <p>Participants will no longer be able to submit or change answers.</p>
    {#if surveyActionError}<p class="orange">{surveyActionError}</p>{/if}
  </div>
</Modal>

<Modal
  actions={false}
  open={shareProjectModalOpen}
  preventCloseOnClickOutside
  modalHeading="Share Project"
  primaryButtonText="Share"
  secondaryButtonText="Cancel"
  on:click:button--secondary={() => {
    shareProjectModalOpen = false;
  }}
  on:open
  on:close={() => {
    shareProjectModalOpen = false;
  }}
  on:submit={shareProject}
>
  <Grid fullWidth>
    <Row>
      <Column>
        <h4>Select Users that can View</h4>
        <DataTable
          headers={[
            { key: "name", value: "Name" },
            { key: "email", value: "Email" },
          ]}
          rows={shareProjectData}
          selectable
          sortable
          selectedRowIds={shareProjectViewers}
        />
      </Column>
      <Column>
        <h4>Select Users that can Edit</h4>
        <DataTable
          headers={[
            { key: "name", value: "Name" },
            { key: "email", value: "Email" },
          ]}
          rows={shareProjectData}
          selectable
          sortable
          selectedRowIds={shareProjectEditors}
        />
      </Column>
    </Row>
  </Grid>
</Modal>

<CreateProjectModal
  bind:open={createProjectModalOpen}
  {projectToEdit}
  {typeOptions}
  existingProjects={[...projects.analysis, ...projects.running]}
  on:created={handleProjectCreated}
  on:updated={handleProjectUpdated}
  on:delete={(event) =>
    deleteProject(
      event.detail.id,
      event.detail.status === "Analysis" ? "analysis" : "running",
    )}
/>

<Modal
  open={deleteProjectModalOpen}
  preventCloseOnClickOutside
  modalHeading={deleteProjectIsPotentialScout
    ? "Delete Survey?"
    : "Delete Project?"}
  primaryButtonText="Delete"
  secondaryButtonText="Cancel"
  actions
  width="undefined"
  primaryButtonAction={confirmDeleteProject}
  primaryButtonValidations={deleteProjectNameMismatch}
  on:click:button--secondary={() => {
    deleteProjectModalOpen = false;
  }}
  on:open
  on:close={() => {
    deleteProjectModalOpen = false;
  }}
>
  <p class="deleteModalBody">
    Are you sure you want to delete the {deleteProjectIsPotentialScout
      ? "survey"
      : "project"}: <span class="project-name">{deleteProjectName}</span>?
    <span>This action cannot be undone.</span>
  </p>
  <TextInput
    labelText={`Type "${deleteProjectName}" to confirm`}
    placeholder="Enter the exact name"
    bind:value={deleteProjectConfirmName}
    invalid={deleteProjectConfirmName.length > 0 && deleteProjectNameMismatch}
    invalidText="The name does not match."
  />
</Modal>

<Modal
  open={cloneProjectModalOpen}
  actions={false}
  modalHeading="Clone Project?"
  primaryButtonText="Clone"
  preventCloseOnClickOutside
  secondaryButtonText="Cancel"
  on:click:button--secondary={() => {
    cloneProjectModalOpen = false;
  }}
  on:open
  on:close={() => {
    cloneProjectModalOpen = false;
  }}
  on:submit={confirmCloneProject}
>
  <TextInput labelText="New Project Name" bind:value={cloneProjectName} />
</Modal>

<style>
  .main {
    position: relative;
    display: flex;
    flex-direction: column;
    max-width: 100vw;
    width: 100%;
    gap: 1rem;
    padding: 0;
    min-height: calc(100vh - 4rem);
    background: var(--pax-white, #f9fbff);
  }

  :global(.header_new_project button),
  :global(.header_new_project .button-md),
  :global(.header_new_project .button-secondary) {
    height: 2.5rem !important;
    min-height: 2.5rem !important;
  }

  .projects_overview_layout {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    align-items: stretch;
    gap: 0;
    flex: 1;
    min-height: calc(100vh - 4rem);
  }

  .digital_twin_section {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    min-height: calc(100vh - 4rem);
    background: transparent;
    color: var(--pax-black, #1c1c20);
  }

  .intelligence_grid {
    display: flex;
    flex-direction: column;
    container-type: inline-size;
    gap: var(--control-gap);
    flex: 1;
    min-height: 0;
    box-sizing: border-box;
    padding: 1rem;
    width: 100%;
  }

  @media (max-width: 64rem) {
    .projects_overview_layout {
      grid-template-columns: repeat(3, minmax(18rem, 1fr));
    }

    .digital_twin_section {
      min-height: 18rem;
      grid-column: 1 / -1;
    }

    .intelligence_grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 48rem) {
    .projects_overview_layout {
      grid-template-columns: 1fr;
    }

    .digital_twin_section {
      grid-column: auto;
    }

    .intelligence_grid {
      grid-template-columns: 1fr;
    }
  }

  .deleteModalBody {
    padding: 23px 0px 23px 0px;
    font-family: Inter;
    font-size: 14px;
  }

  .deleteModalBody span {
    font-weight: 700;
  }

  .deleteModalBody :last-child {
    color: var(--pax-orange);
  }

  .deleteModalBody .project-name {
    font-weight: bold;
  }

  .side-header {
    width: 100%;
    overflow: hidden;
    min-width: 0;
  }

  .side-header-name {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    min-width: 0;
  }

  .side-project-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .side-header-right {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    flex-shrink: 0;
  }

  .side-header-right :global(.button_wrapper.small img) {
    width: 1rem;
    height: 1rem;
    object-fit: contain;
  }

  .project-status-indicator {
    display: inline-block;
    width: 0.75rem;
    height: 0.75rem;
    flex-shrink: 0;
    border-radius: 50%;
    background: #8ef095;
    box-shadow: 0 0 0 2px rgba(142, 240, 149, 0.2);
    cursor: help;
    margin-right: 0.25rem;
  }

  .side-title {
    color: var(--pax-black, #1c1c20);
    font-size: 0.75rem;
    font-style: normal;
    font-weight: 700;
    line-height: 120%; /* 0.9rem */
    margin-bottom: 0.5rem;
  }

  .side-container {
    width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1.81rem;
    margin-top: 1rem;
    overflow-x: hidden;
  }

  .side-content {
    max-width: 100%;
    margin: 0;
    color: var(--pax-black, #1c1c20);
    font-size: 0.75rem;
    line-height: 1.3;
    overflow-wrap: anywhere;
    word-break: normal;
  }

  .side_link {
    padding: 0;
    border: none;
    background: none;
    font-family: inherit;
    text-align: left;
    text-decoration: underline;
    cursor: pointer;
  }

  .survey_invitation_badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    max-width: 100%;
    min-width: 0;
  }

  .side-container :global(.badge_color) {
    max-width: 100%;
    min-width: 0;
    flex-shrink: 1;
  }

  .side-container :global(.badge_color span) {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .side_title_with_tooltip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    margin-bottom: 0.5rem;
  }

  .side_title_with_tooltip .side-title {
    margin-bottom: 0;
  }

  .survey_invitation_action {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .survey_overview_list {
    display: flex;
    flex-direction: column;
    gap: 1.81rem;
  }

  .side_footer_actions {
    width: 100%;
    min-width: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .side_footer_actions :global(button) {
    min-width: 0;
    flex: 1 1 8rem;
    white-space: nowrap;
  }

  .survey_send_warning {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .survey_send_warning p {
    margin: 0;
  }

  :global(.edit-modal .bx--modal-content) {
    overflow-y: visible !important;
  }
</style>
